import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

export const travelKeys = {
    all: ['travel'],
    requests: (orgId) => [...travelKeys.all, 'requests', orgId],
    request: (id) => [...travelKeys.all, 'request', id],
    budgets: (orgId) => [...travelKeys.all, 'budgets', orgId],
    budget: (id) => [...travelKeys.all, 'budget', id],
    advances: (orgId) => [...travelKeys.all, 'advances', orgId],
    advance: (id) => [...travelKeys.all, 'advance', id],
    prvs: (orgId) => [...travelKeys.all, 'prvs', orgId],
    prv: (id) => [...travelKeys.all, 'prv', id],
    pendingRequests: (orgId) => [...travelKeys.all, 'pending-requests', orgId],
    pendingBudgets: (orgId) => [...travelKeys.all, 'pending-budgets', orgId],
    pendingAdvances: (orgId) => [...travelKeys.all, 'pending-advances', orgId],
    pendingPrvs: (orgId) => [...travelKeys.all, 'pending-prvs', orgId],
};

// ─── TRAVEL REQUESTS ────────────────────────────────────────────────────────

export function useMyTravelRequests(orgId) {
    return useQuery({
        queryKey: travelKeys.requests(orgId),
        queryFn: () => apiHelper.getResource('/trv/requests/', { orgId }),
        enabled: !!orgId,
        retry: false,
    });
}

export function useTravelRequest(id, orgId) {
    return useQuery({
        queryKey: travelKeys.request(id),
        queryFn: () => apiHelper.getResource(`/trv/requests/${id}/`, { orgId }),
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useCreateTravelRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => apiHelper.postResource('/trv/requests/', data, { orgId }),
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.requests(orgId) });
        },
    });
}

export function useUpdateTravelRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.patchResource(`/trv/requests/${id}/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.request(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.requests(orgId) });
        },
    });
}

export function useSubmitTravelRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.postResource(`/trv/requests/${id}/submit/`, {}, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.request(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.requests(orgId) });
        },
    });
}

export function useApproveTravelRequestLM() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/requests/${id}/approve_lm/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.request(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.requests(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingRequests(orgId) });
        },
    });
}

export function useApproveTravelRequestSM() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/requests/${id}/approve_sm/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.request(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.requests(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingRequests(orgId) });
        },
    });
}

export function usePendingTravelRequests(orgId) {
    return useQuery({
        queryKey: travelKeys.pendingRequests(orgId),
        queryFn: () => apiHelper.getResource('/trv/requests/pending/', { orgId }),
        enabled: !!orgId,
        retry: false,
    });
}

// ─── TRAVEL BUDGETS ──────────────────────────────────────────────────────────

export function useCreateTravelBudget() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => apiHelper.postResource('/trv/budgets/', data, { orgId }),
        onSuccess: (responseData, { data, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.budgets(orgId) });
            if (data?.travel_request_id) {
                queryClient.invalidateQueries({ queryKey: travelKeys.request(data.travel_request_id) });
            }
        },
    });
}

export function useTravelBudget(id, orgId) {
    return useQuery({
        queryKey: travelKeys.budget(id),
        queryFn: () => apiHelper.getResource(`/trv/budgets/${id}/`, { orgId }),
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useTravelBudgetByRequest(requestId, orgId) {
    return useQuery({
        queryKey: [...travelKeys.all, 'budget-by-request', requestId],
        queryFn: async () => {
            const res = await apiHelper.getResource(`/trv/budgets/`, { orgId, params: { travel_request: requestId } });
            // API may return a list or a single object
            if (Array.isArray(res)) return res[0] || null;
            return res?.results?.[0] || res || null;
        },
        enabled: !!orgId && !!requestId,
        retry: false,
    });
}

export function useSubmitTravelBudget() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.postResource(`/trv/budgets/${id}/submit/`, {}, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.budget(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.budgets(orgId) });
        },
    });
}

export function useApproveTravelBudgetLM() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/budgets/${id}/approve_lm/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.budget(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.budgets(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingBudgets(orgId) });
        },
    });
}

export function useApproveTravelBudgetFinance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/budgets/${id}/approve_finance/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.budget(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.budgets(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingBudgets(orgId) });
        },
    });
}

export function usePendingTravelBudgets(orgId) {
    return useQuery({
        queryKey: travelKeys.pendingBudgets(orgId),
        queryFn: () => apiHelper.getResource('/trv/budgets/pending/', { orgId }),
        enabled: !!orgId,
        retry: false,
    });
}

// ─── TRAVEL ADVANCES ─────────────────────────────────────────────────────────

export function useCreateTravelAdvance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => apiHelper.postResource('/trv/advances/', data, { orgId }),
        onSuccess: (_, { data, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.advances(orgId) });
            if (data?.travel_request_id) {
                queryClient.invalidateQueries({ queryKey: travelKeys.request(data.travel_request_id) });
            }
        },
    });
}

export function useTravelAdvance(id, orgId) {
    return useQuery({
        queryKey: travelKeys.advance(id),
        queryFn: () => apiHelper.getResource(`/trv/advances/${id}/`, { orgId }),
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useTravelAdvanceByRequest(requestId, orgId) {
    return useQuery({
        queryKey: [...travelKeys.all, 'advance-by-request', requestId],
        queryFn: async () => {
            const res = await apiHelper.getResource(`/trv/advances/`, { orgId, params: { travel_request: requestId } });
            if (Array.isArray(res)) return res[0] || null;
            return res?.results?.[0] || res || null;
        },
        enabled: !!orgId && !!requestId,
        retry: false,
    });
}

export function useSubmitTravelAdvance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.postResource(`/trv/advances/${id}/submit/`, {}, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.advance(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.advances(orgId) });
        },
    });
}

export function useApproveTravelAdvanceFinance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/advances/${id}/approve_finance/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.advance(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.advances(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingAdvances(orgId) });
        },
    });
}

export function useApproveTravelAdvancePartner() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/advances/${id}/approve_partner/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.advance(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.advances(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingAdvances(orgId) });
        },
    });
}

export function useMarkTravelAdvancePaid() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.postResource(`/trv/advances/${id}/mark_paid/`, {}, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.advance(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.advances(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingAdvances(orgId) });
        },
    });
}

export function usePendingTravelAdvances(orgId) {
    return useQuery({
        queryKey: travelKeys.pendingAdvances(orgId),
        queryFn: () => apiHelper.getResource('/trv/advances/pending/', { orgId }),
        enabled: !!orgId,
        retry: false,
    });
}

// ─── TRAVEL PRVs ─────────────────────────────────────────────────────────────

export function useCreateTravelPRV() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => apiHelper.postResource('/trv/prvs/', data, { orgId }),
        onSuccess: (_, { data, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.prvs(orgId) });
            if (data?.travel_request_id) {
                queryClient.invalidateQueries({ queryKey: travelKeys.request(data.travel_request_id) });
            }
        },
    });
}

export function useTravelPRV(id, orgId) {
    return useQuery({
        queryKey: travelKeys.prv(id),
        queryFn: () => apiHelper.getResource(`/trv/prvs/${id}/`, { orgId }),
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useTravelPRVByRequest(requestId, orgId) {
    return useQuery({
        queryKey: [...travelKeys.all, 'prv-by-request', requestId],
        queryFn: async () => {
            const res = await apiHelper.getResource(`/trv/prvs/`, { orgId, params: { travel_request: requestId } });
            if (Array.isArray(res)) return res[0] || null;
            return res?.results?.[0] || res || null;
        },
        enabled: !!orgId && !!requestId,
        retry: false,
    });
}

export function useSubmitTravelPRV() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.postResource(`/trv/prvs/${id}/submit/`, {}, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.prv(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.prvs(orgId) });
        },
    });
}

export function useApproveTravelPRVFinance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/prvs/${id}/approve_finance/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.prv(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.prvs(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingPrvs(orgId) });
        },
    });
}

export function useApproveTravelPRVPartner() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.postResource(`/trv/prvs/${id}/approve_partner/`, data, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.prv(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.prvs(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingPrvs(orgId) });
        },
    });
}

export function useMarkTravelPRVPaid() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => apiHelper.postResource(`/trv/prvs/${id}/mark_paid/`, {}, { orgId }),
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: travelKeys.prv(id) });
            queryClient.invalidateQueries({ queryKey: travelKeys.prvs(orgId) });
            queryClient.invalidateQueries({ queryKey: travelKeys.pendingPrvs(orgId) });
        },
    });
}

export function usePendingTravelPRVs(orgId) {
    return useQuery({
        queryKey: travelKeys.pendingPrvs(orgId),
        queryFn: () => apiHelper.getResource('/trv/prvs/pending/', { orgId }),
        enabled: !!orgId,
        retry: false,
    });
}
